import { Plus, Settings01 } from "@untitledui/icons";
import { memo, type ReactNode, useCallback, useMemo, useState } from "react";
import { useCreateAndAssignCreatorTag, useCreatorTags, useSaveCreatorTagAssignments } from "@/api/creator-tags";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { randomTagColor } from "@/lib/creator-tags/colors";

// Mirrors the server-side limit (`createTagSchema` in packages/api .../creatorTags/routes.ts).
const MAX_TAG_NAME_LENGTH = 50;

type TagOption = { id: string; name: string; color: string };

const TagOptionRow = memo(
  ({ tag, checked, onToggle }: { tag: TagOption; checked: boolean; onToggle: (id: string) => void }) => {
    const handleSelect = useCallback(() => onToggle(tag.id), [onToggle, tag.id]);

    return (
      <CommandItem value={tag.id} onSelect={handleSelect} className="gap-2">
        <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
        <span className="truncate">{tag.name}</span>
      </CommandItem>
    );
  },
);
TagOptionRow.displayName = "TagOptionRow";

export interface TagPickerPopoverProps {
  tiktokCreatorOpenId: string;
  assignedTagIds: string[];
  onManageAllTags: () => void;
  /**
   * Fires whenever the staged selection changes so the cell can preview the
   * tags inline before the diff is committed on close. Called with the staged
   * id list while open, and `null` on close to fall back to the server ids.
   */
  onStagedChange?: (ids: string[] | null) => void;
  children: ReactNode;
}

export const TagPickerPopover = ({
  tiktokCreatorOpenId,
  assignedTagIds,
  onManageAllTags,
  onStagedChange,
  children,
}: TagPickerPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // `baseline` = what's persisted on the server; `staged` = the working set the
  // checkboxes bind to. Toggling a checkbox only mutates `staged`; the diff is
  // committed against `baseline` on close.
  const [baselineIds, setBaselineIds] = useState<Set<string>>(new Set());
  const [stagedIds, setStagedIds] = useState<Set<string>>(new Set());

  const { data: allTags = [] } = useCreatorTags();
  const saveAssignments = useSaveCreatorTagAssignments();
  const createAndAssign = useCreateAndAssignCreatorTag(tiktokCreatorOpenId);

  const toggle = useCallback(
    (id: string) => {
      setStagedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        onStagedChange?.([...next]);
        return next;
      });
    },
    [onStagedChange],
  );

  const query = search.trim();
  const filtered = useMemo(() => {
    const lower = query.toLowerCase();
    return allTags.filter((tag) => tag.name.toLowerCase().includes(lower));
  }, [allTags, query]);

  // Only offer "Create" when the query doesn't exactly match an existing tag
  // (case-insensitive), since duplicate names are rejected server-side.
  const hasExactMatch = useMemo(
    () => allTags.some((tag) => tag.name.toLowerCase() === query.toLowerCase()),
    [allTags, query],
  );
  const showCreate = query.length > 0 && !hasExactMatch;

  const handleCreate = useCallback(async () => {
    const name = query;
    if (!name) return;
    try {
      const tag = await createAndAssign.mutateAsync({ name, color: randomTagColor() });
      // The tag is already persisted + assigned, so fold it into both sets so it
      // isn't re-sent in the close diff and shows checked immediately.
      setBaselineIds((prev) => new Set(prev).add(tag.id));
      setStagedIds((prev) => {
        const next = new Set(prev).add(tag.id);
        onStagedChange?.([...next]);
        return next;
      });
      setSearch("");
    } catch {
      // toast handled in the hook
    }
  }, [allTags, createAndAssign, query]);

  const commit = useCallback(() => {
    const add = [...stagedIds].filter((id) => !baselineIds.has(id));
    const remove = [...baselineIds].filter((id) => !stagedIds.has(id));
    if (add.length > 0 || remove.length > 0) {
      saveAssignments.mutate({ tiktokCreatorOpenIds: [tiktokCreatorOpenId], add, remove });
    }
  }, [stagedIds, baselineIds, saveAssignments, tiktokCreatorOpenId]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        // Seed the staged/baseline sets from the current assignments on open.
        setBaselineIds(new Set(assignedTagIds));
        setStagedIds(new Set(assignedTagIds));
        setSearch("");
        onStagedChange?.(assignedTagIds);
      } else {
        // Closing (outside click / Escape / anchor click) commits the staged diff.
        commit();
        // Hand the row back to the server ids; the commit's optimistic patch
        // already reflects the change.
        onStagedChange?.(null);
      }
      setOpen(next);
    },
    [commit, assignedTagIds, onStagedChange],
  );

  return (
    // `modal` so an outside click is consumed to dismiss the picker and never
    // reaches the table row underneath (which would otherwise open the drawer).
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            autoFocus
            maxLength={MAX_TAG_NAME_LENGTH}
            placeholder="Search or type to create a tag..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {!showCreate && filtered.length === 0 && (
              <CommandEmpty>No results found. Start typing to create a new one.</CommandEmpty>
            )}
            {showCreate && (
              <CommandGroup>
                <CommandItem value={`__create__${query}`} onSelect={handleCreate} className="gap-2">
                  <Plus className="size-4 shrink-0" />
                  <span className="truncate">
                    Create <span className="font-medium">“{query}”</span>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((tag) => (
                  <TagOptionRow key={tag.id} tag={tag} checked={stagedIds.has(tag.id)} onToggle={toggle} />
                ))}
              </CommandGroup>
            )}
          </CommandList>
          <button
            type="button"
            onClick={() => {
              // Commit staged changes before navigating away to the library.
              handleOpenChange(false);
              onManageAllTags();
            }}
            className="flex w-full items-center gap-2 border-t border-secondary px-3 py-2 text-sm text-tertiary hover:bg-secondary"
          >
            <Settings01 className="size-4 shrink-0" />
            Manage All Tags
          </button>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
